import { Component, SimpleChanges, ViewChild, Inject, EventEmitter, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { merge, Observable, Subscription } from 'rxjs';
import * as _ from 'lodash';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import { debounceTime, filter, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';

import { ComponentBase } from '@framework/base/component';
import { SearchBoxStateViewModel, CompanyViewModel } from '@modules/company/models/store-view-models';
import { CoreState } from '@app/store/core/states';
import { searchBoxSelectors, companySelectors } from '@modules/company/store/selectors';
import { searchBoxActions } from '@modules/company/store/actions';
import { ModalService } from '@framework/services';
import { SearchStoryInputApiModel } from '@modules/story/models/api-models/input';
import { StoryFacetedSearchViewModel, StoryFacetedSearchCampaignItemViewModel, StoryFacetedSearchPeopleItemViewModel, StoryFacetedSearchTagItemViewModel, StoryLoadStatusViewModel } from '@modules/story/models/store-view-models';
import { storySelector } from '@modules/story/store/selectors';
import { SelectedModel } from '@framework/models/commons';
import { UserProfileViewModel } from '@modules/user/store/models';
import { userProfileSelectors } from '@modules/user/store/selectors';
import { storyActions } from '@modules/story/store/actions';
import { SaveLandingPageModalContainerComponent } from '@modules/company/components/save-landing-page-modal-container/save-landing-page-modal-container.component';
import { LandingPageQueryObjectsModel } from '@modules/company/models/commons';
import { OwlCarouselComponent } from '@modules/shared/components/commons/owl-carousel/owl-carousel.component';
import { ShareModalContainerComponent } from '@modules/shared/components/commons/share-modal-container/share-modal-container.component';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'co-pub-search-box',
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
  animations: [
    trigger('showhide', [
      // ...
      state('show', style({
        opacity: 1,
        zIndex: 1000
      })),
      state('hide', style({
        opacity: 0,
        zIndex: -1
      })),
      transition('show => hide', [
        animate('0.5s ease-in-out')
      ]),
      transition('hide => show', [
        animate('0.5s ease-in-out')
      ]),
    ]),
  ]
})
export class SearchBoxComponent extends ComponentBase {
  @ViewChild('owlElement') owlElement: OwlCarouselComponent;
  @ViewChild('noOptions') noOptions;
  @Input() campaignId: any;
  @Input() campaignSlug: any;
  // Private variables
  private selectSearchBoxStatus$: Observable<SearchBoxStateViewModel>;
  private selectFacetedSearchResult$: Observable<StoryFacetedSearchViewModel>;
  private selectStoryLoadStatus$: Observable<StoryLoadStatusViewModel>;
  private selectCurrentUserProfile$: Observable<UserProfileViewModel>;
  private selectCurrentCompany$: Observable<CompanyViewModel>;

  private selectCurrentUserProfileSubscription: Subscription;
  private selectSearchBoxStatusSubscription: Subscription;
  private selectFacetedSearchResultSubscription: Subscription;
  private searchFormChangeSubscription: Subscription;
  private filterSearchSubscription: Subscription;
  private facetFilterSubscriptions: Subscription[] = [];
  private searchTextChangeSubscription: Subscription;
  private searchTextClearSubscription: Subscription;
  private selectStoryLoadStatusSubscription: Subscription;
  private searchActionSubscription: Subscription;
  private clearActionSubscription: Subscription;
  private selectCurrentCompanySubscription: Subscription;
  private searchTextEvent: EventEmitter<string> = new EventEmitter();

  private queryStoriesData: SearchStoryInputApiModel = new SearchStoryInputApiModel();
  private preventFormTrigger: boolean = false;
  private isSearchTextChange: boolean = false;
  private facetUpdateToken: number = 0;
  private hasLoadedStoriesForCompany: boolean = false;
  private blockFilterSearchUntil: number = 0;
  // Public variables
  public searchBoxStatus: SearchBoxStateViewModel;
  public owlOptions: any = {
    dots: false,
    autoWidth: true,
    loop: false,
    rewind: true,
    nav: true,
  items: 10
  };
  public searchForm: UntypedFormGroup;
  public facetedTags: StoryFacetedSearchTagItemViewModel[];
  public facetedCampaigns: StoryFacetedSearchCampaignItemViewModel[];
  public facetedPeoples: StoryFacetedSearchPeopleItemViewModel[];
  public facetedTagSelected: StoryFacetedSearchTagItemViewModel[];
  public facetedCampaignSelected: StoryFacetedSearchCampaignItemViewModel[];
  public facetedPeopleSelected: StoryFacetedSearchPeopleItemViewModel[];
  public storyLoadStatus: StoryLoadStatusViewModel;
  public currentUserProfile: UserProfileViewModel;
  public currentCompany: CompanyViewModel = new CompanyViewModel();
  public searchText: string;
  public inputFocus: boolean = true;

  public isShowForm: boolean = true;
  public insideIframe:boolean = false;
  constructor(
    private store$: Store<CoreState>,
    private modalService: ModalService,
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions) {
    super();
    this.initForm();
    if (window.location !== window.parent.location) {
      this.insideIframe = true;
      //document.getElementsByClassName("site-container")[0]['style'].padding = 0;
    } else {
      this.insideIframe = false;
    }
    this.captureParams();

    this.selectCurrentUserProfile$ = this.store$.select(userProfileSelectors.selectCurrentProfile);
    this.selectSearchBoxStatus$ = this.store$.select(searchBoxSelectors.selectSearchBoxStatus);
    this.selectFacetedSearchResult$ = this.store$.select(storySelector.selectFacetedSearchResults);
    this.selectStoryLoadStatus$ = this.store$.select(storySelector.selectLoadStatus);
    this.selectCurrentCompany$ = this.store$.select(companySelectors.selectSelectedCompany);
  }

  // Life cycle hook
  protected onInit(): void {
    this.subscribe();
  }
  protected onDestroy(): void {
    this.unsubscribe();
  }

  protected onChanges(): void {
    if(this.campaignId){
      this.queryStoriesData.campaigns = [this.campaignId]
      this.loadStories();
    }
  }
  protected onDoCheck(): void {

  }
  protected onAfterContentInit(): void {

  }
  protected onAfterContentChecked(): void {

  }
  protected onAfterViewInit(): void {

  }
  protected onAfterViewChecked(): void {

  }
  // Private functions
  private subscribe() {
    this.selectCurrentUserProfileSubscription = this.selectCurrentUserProfile$.subscribe((userProfile) => {
      if (userProfile) {
        this.currentUserProfile = userProfile;
      }
    });

    this.selectCurrentCompanySubscription = this.selectCurrentCompany$.subscribe((currentCompany) => {
      if (currentCompany) {
        this.currentCompany = currentCompany;
        this.queryStoriesData.pagination.sort = 'desc';
        if (!this.hasLoadedStoriesForCompany) {
          this.hasLoadedStoriesForCompany = true;
          this.loadStories();
        }
      }
    });

    this.selectSearchBoxStatusSubscription = this.selectSearchBoxStatus$.subscribe((state: SearchBoxStateViewModel) => {
      this.searchBoxStatus = state;
    });

    this.selectFacetedSearchResultSubscription = this.selectFacetedSearchResult$.subscribe((result: StoryFacetedSearchViewModel) => {
      if (!result) {
        return;
      }
      if (this.queryStoriesData.pagination.page > 1) {
        return;
      }
      this.updateSearchForm(result);
    });

    this.searchFormChangeSubscription = this.searchForm.valueChanges.pipe(
      filter(() => !this.preventFormTrigger)
    ).subscribe(() => {
      this.getSelectedValues();
    });

    this.searchTextChangeSubscription = this.searchTextEvent.pipe(
      tap(() => {
        this.isSearchTextChange = true;
      }),
      filter(() => !this.preventFormTrigger),
      debounceTime(500)
    ).subscribe((_searchText) => {
      this.searchText = _searchText;

      this.queryStoriesData.pagination.page = 1;

      this.resetFacetedSeletected();
      this.buildSearch();
      this.search();
      this.isSearchTextChange = false;
    });

    this.searchTextClearSubscription = this.searchForm.get('searchText').valueChanges.pipe(
      filter(() => !this.preventFormTrigger),
      debounceTime(500)
    ).subscribe((value: string) => {
      const next = (value || '').trim();
      if (!next && this.searchText) {
        this.searchTextEvent.emit('');
      }
    });

    this.filterSearchSubscription = merge(
      this.searchForm.get('includeStories').valueChanges,
      this.searchForm.get('includeTestimonials').valueChanges
    ).pipe(
      filter(() => !this.preventFormTrigger && !this.isSearchTextChange),
      filter(() => Date.now() >= this.blockFilterSearchUntil),
      debounceTime(300),
      filter(() => !this.preventFormTrigger && Date.now() >= this.blockFilterSearchUntil)
    ).subscribe(() => {
      this.runFilterSearch();
    });

    this.selectStoryLoadStatusSubscription = this.selectStoryLoadStatus$.subscribe((status) => {
      this.storyLoadStatus = status;
    });

    this.searchActionSubscription = this.actions$.pipe(
      ofType<searchBoxActions.Search>(searchBoxActions.ActionTypes.SEARCH)
    ).subscribe((action) => {
      if (action.searchPayload) {
        this.queryStoriesData = Object.assign(this.queryStoriesData, action.searchPayload);
      }
      if (this.queryStoriesData.pagination && this.queryStoriesData.pagination.page > 1) {
        this.blockFilterSearchUntil = Date.now() + 2500;
      }
      this.loadStories();
    });

    this.clearActionSubscription = this.actions$.pipe(
      ofType<searchBoxActions.Clear>(searchBoxActions.ActionTypes.CLEAR)
    ).subscribe((action) => {
      this.preventFormTrigger = true;
      this.searchForm.patchValue({
        searchText: '',
        includeStories: true,
        includeTestimonials: true
      });
      this.searchText = '';
      this.queryStoriesData.keywords = [];
      this.resetFacetedSeletected();
      this.clearBrowserSearchParams();
      this.preventFormTrigger = false;
      this.searchTextEvent.emit('');
      this.store$.dispatch(new searchBoxActions.Hide());
    });
  }
  private unsubscribe() {
    this.selectSearchBoxStatusSubscription.unsubscribe();
    this.selectFacetedSearchResultSubscription.unsubscribe();
    this.searchFormChangeSubscription.unsubscribe();
    this.selectStoryLoadStatusSubscription.unsubscribe();
    if (this.filterSearchSubscription) {
      this.filterSearchSubscription.unsubscribe();
    }
    this.clearFacetFilterSubscriptions();
    this.selectCurrentUserProfileSubscription.unsubscribe();
    this.selectCurrentCompanySubscription.unsubscribe();
    this.searchActionSubscription.unsubscribe();
    this.clearActionSubscription.unsubscribe();
    this.searchTextChangeSubscription.unsubscribe();
    if (this.searchTextClearSubscription) {
      this.searchTextClearSubscription.unsubscribe();
    }
  }

  private runFilterSearch() {
    this.queryStoriesData.pagination.page = 1;
    this.buildSearch();
    this.search();
  }

  private clearFacetFilterSubscriptions() {
    this.facetFilterSubscriptions.forEach((sub) => sub.unsubscribe());
    this.facetFilterSubscriptions = [];
  }

  private bindFacetFilterSearches() {
    this.clearFacetFilterSubscriptions();
    ['tags', 'campaigns', 'peoples'].forEach((name) => {
      const control = this.searchForm.get(name);
      if (!control) {
        return;
      }
      this.facetFilterSubscriptions.push(
        control.valueChanges.pipe(
          filter(() => !this.preventFormTrigger && !this.isSearchTextChange),
          filter(() => Date.now() >= this.blockFilterSearchUntil),
          debounceTime(300),
          filter(() => !this.preventFormTrigger && Date.now() >= this.blockFilterSearchUntil)
        ).subscribe(() => {
          this.runFilterSearch();
        })
      );
    });
  }

  private initForm() {
    this.searchForm = this.formBuilder.group({
      searchText: ['', Validators.minLength(3)],
      includeStories: [true],
      includeTestimonials: [true],
      isMyStoryOnly: [false]
    });
  }

  private createFacetedControl(facetedSearchItems: SelectedModel[]) {
    const fArr = facetedSearchItems.map(item => {
      return new UntypedFormControl(item.selected || false);
    });
    return new UntypedFormArray(fArr);
  }

  private updateSearchForm(facetedSearchResult: StoryFacetedSearchViewModel) {
    this.preventFormTrigger = true;
    const token = ++this.facetUpdateToken;

    this.facetedTags = facetedSearchResult.tags || [];
    this.searchForm.setControl('tags', this.createFacetedControl(this.facetedTags));

    this.facetedCampaigns = facetedSearchResult.campaigns || [];
    this.searchForm.setControl('campaigns', this.createFacetedControl(this.facetedCampaigns));

    this.facetedPeoples = facetedSearchResult.players || [];
    this.searchForm.setControl('peoples', this.createFacetedControl(this.facetedPeoples));

    this.getSelectedValues(false);
    this.bindFacetFilterSearches();
    setTimeout(() => {
      if (token === this.facetUpdateToken) {
        this.preventFormTrigger = false;
      }
    });
  }

  private buildSearch() {
    const keyword = (this.searchText || '').trim();
    this.queryStoriesData.keywords = keyword ? [keyword] : [];
    this.queryStoriesData.campaigns = this.facetedCampaignSelected ? this.facetedCampaignSelected.map(m => m.id) : [];
    this.queryStoriesData.tags = this.facetedTagSelected ? this.facetedTagSelected.map(m => m.name) : [];
    this.queryStoriesData.peoples = this.facetedPeopleSelected ? this.facetedPeopleSelected.map(m => m.id) : [];
    this.queryStoriesData.includeStory = this.searchForm.controls.includeStories.value;
    this.queryStoriesData.includeTestimonial = this.searchForm.controls.includeTestimonials.value;
  }

  private getSelectedValues(reinitCarousel: boolean = true) {
    window.document.querySelectorAll('.dropdown-select.show, .dropdown-menu.show').forEach(item => {
      item.classList.remove('show');
    });

    // Tag
    if (this.searchForm.controls.tags) {
      this.facetedTagSelected = [];

      this.searchForm.controls.tags["controls"].forEach((tag, i) => {
        if (tag.value === true) {
          this.facetedTagSelected.push(this.facetedTags[i]);
        }
      });
      if(this.facetedTagSelected.length){
        var payload = {tags:this.facetedTagSelected.map(m => m.name)}
        this.store$.dispatch(new searchBoxActions.SearchPayload(payload))
      }
      else{
        this.store$.dispatch(new searchBoxActions.SearchPayload(null))
      }
    }

    // Campaign
    if (this.searchForm.controls.campaigns) {
      this.facetedCampaignSelected = [];
      this.searchForm.controls.campaigns["controls"].forEach(
        (campaign, i) => {
          if (campaign.value === true) {
            this.facetedCampaignSelected.push(this.facetedCampaigns[i]);
          }
        }
      );
    }

    // People
    if (this.searchForm.controls.peoples) {
      this.facetedPeopleSelected = [];

      this.searchForm.controls.peoples["controls"].forEach(
        (people, i) => {
          if (people.value === true) {
            this.facetedPeopleSelected.push(this.facetedPeoples[i]);
          }
        }
      );
    }

    if (reinitCarousel && this.owlElement) {
      this.owlElement.reInit();
    }
  }

  private loadStories() {
    this.queryStoriesData.companyId = this.currentCompany.id;
    this.queryStoriesData.userId = this.currentUserProfile && this.currentUserProfile.id;
    this.queryStoriesData.pagination.sortBy = 'published_at';
    this.queryStoriesData.public = true;
    this.buildFilter();
    if(this.campaignId){
      this.queryStoriesData.campaigns = [this.campaignId]
    }
    this.store$.dispatch(new storyActions.LoadByCompany(this.queryStoriesData));
  }

  private buildFilter() {
    if (!this.searchBoxStatus || !this.searchBoxStatus.isSearching) return;
    this.syncBrowserUrlWithSearch();
  }

  private clearBrowserSearchParams() {
    const baseUrl = this.campaignId
      ? `/c/${this.currentCompany.slug}/campaigns/${this.campaignSlug}`
      : `/c/${this.currentCompany.slug}`;
    this.updateBrowserUrl(baseUrl);
  }

  private syncBrowserUrlWithSearch() {
    const baseUrl = this.campaignId
      ? `/c/${this.currentCompany.slug}/campaigns/${this.campaignSlug}`
      : `/c/${this.currentCompany.slug}`;
    let queryString = (this.queryStoriesData.queryWithoutPaination || '').replace(/^&+/, '');
    // Public company pages always carry public=1 in API queries; don't leave a bare ?public=1 in the URL.
    if (!queryString || queryString === 'public=1') {
      this.updateBrowserUrl(baseUrl);
      return;
    }
    const url = `${baseUrl}?${queryString}`;
    this.updateBrowserUrl(url);
  }

  private updateBrowserUrl(url) {
    window.history.pushState({}, 'GoodSeeker', url);
  }

  private showSearchBox() {
    this.store$.dispatch(new searchBoxActions.Show());
  }

  private captureParams() {
    var queryParams = this.activatedRoute.snapshot.queryParams;
    if(queryParams){
      if(Object.keys(queryParams).length == 1 && Object.keys(queryParams).includes('sb')){
        queryParams = {}
      }
    }

    if (!_.isEmpty(queryParams)) {
      var storekeysParms = Object.keys(queryParams);
      var checkforfilters = (storekeysParms.includes("keyword") || storekeysParms.includes("filter") 
      || storekeysParms.includes("includeTestimonials") || storekeysParms.includes("includeTestimonials") );
      this.queryStoriesData.init(queryParams);
      this.searchForm.patchValue({
        searchText: this.queryStoriesData.keywords[0],
        includeStories: this.queryStoriesData.includeStory,
        includeTestimonials: this.queryStoriesData.includeTestimonial,
        userId: this.queryStoriesData.userId
      });
      this.searchText = this.queryStoriesData.keywords[0];
      this.search();
      if(this.insideIframe && checkforfilters){
        // this.showSearchBox();
      }
    else if(!this.insideIframe){
      // this.showSearchBox();
    }
    }
  }

  private search() {
    this.store$.dispatch(new searchBoxActions.Search(null));
  }

  private resetFacetedSeletected() {
    this.facetedCampaignSelected = [];
    this.facetedPeopleSelected = [];
    this.facetedTagSelected = [];
  }
  // Public functions
  public closeSearchBox() {
    if (this.isShowSearchSelected()) {
      this.isShowForm = false;
    }
    else {
      this.store$.dispatch(new searchBoxActions.Hide());
    }
  }

  public showShareModal() {
    this.modalService.showModal(ShareModalContainerComponent, {}, { animated: false }).then(() => {

    });
  }

  public openSaveLandingPageModal() {
    const initState: LandingPageQueryObjectsModel = {
      queryStories: this.queryStoriesData,
      totalStoriesCount: this.storyLoadStatus.total,
      facetedCampaignSelected: this.facetedCampaignSelected,
      facetedPeopleSelected: this.facetedPeopleSelected,
      facetedTagSelected: this.facetedTagSelected
    };
    this.modalService.showModal(SaveLandingPageModalContainerComponent, { queryObjects: initState }, { class: 'modal-lg' }).then(() => {

    });
  }

  public doSearch() {
    this.searchTextEvent.emit(this.searchForm.controls.searchText.value || '');
  }

  public deleteSearchText() {
    this.preventFormTrigger = true;
    this.searchForm.patchValue({ searchText: '' });
    this.searchText = '';
    this.queryStoriesData.keywords = [];
    this.preventFormTrigger = false;
    this.syncBrowserUrlWithSearch();
    this.searchTextEvent.emit('');
  }

  public isShowSearchFilter() {
    return this.facetedPeoples || this.facetedTags || this.facetedCampaigns;
  }

  public isShowSearchSelected() {
    return this.searchText || this.queryStoriesData.isMyStoriesOnly || !this.queryStoriesData.includeTestimonial || !this.queryStoriesData.includeStory || (this.facetedPeopleSelected && this.facetedPeopleSelected.length) || (this.facetedCampaignSelected && this.facetedCampaignSelected.length) || (this.facetedTagSelected && this.facetedTagSelected.length);
  }

  public tagsTrackByFunc(index: number, item: StoryFacetedSearchTagItemViewModel) {
    return item.name;
  }

  public campaignsTrackByFunc(index: number, item: StoryFacetedSearchCampaignItemViewModel) {
    return item.id;
  }

  public peoplesTrackByFunc(index: number, item: StoryFacetedSearchPeopleItemViewModel) {
    return item.id;
  }

  public clear() {
    this.searchForm.reset();
    this.isShowForm = true;
    this.store$.dispatch(new searchBoxActions.Clear());
  }

  public get tagsFormArr() {
    return this.searchForm.controls['tags'] as UntypedFormArray;
  }

  public get campaignsFormArr() {
    return this.searchForm.controls['campaigns'] as UntypedFormArray;
  }

  public get peoplesFormArr() {
    return this.searchForm.controls['peoples'] as UntypedFormArray;
  }

  public deleteTagItem(index: number, item: StoryFacetedSearchTagItemViewModel) {
    this.facetedTagSelected.splice(index, 1);
    const controlIndex = this.facetedTags.findIndex(tag => tag.name === item.name);
    this.tagsFormArr.controls[controlIndex].setValue(false);
    if (!this.isShowSearchSelected()) {
      this.isShowForm = true;
      this.store$.dispatch(new searchBoxActions.Hide());
    }
  }

  public deleteCampaignItem(index: number, item: StoryFacetedSearchCampaignItemViewModel) {
    this.facetedCampaignSelected.splice(index, 1);
    const controlIndex = this.facetedCampaigns.findIndex(campaign => campaign.id === item.id);
    this.campaignsFormArr.controls[controlIndex].setValue(false);
    if (!this.isShowSearchSelected()) {
      this.isShowForm = true;
      this.store$.dispatch(new searchBoxActions.Hide());
    }
  }

  public deletePeopleItem(index: number, item: StoryFacetedSearchPeopleItemViewModel) {
    this.facetedPeopleSelected.splice(index, 1);
    const controlIndex = this.facetedPeoples.findIndex(people => people.id === item.id);
    this.peoplesFormArr.controls[controlIndex].setValue(false);
    if (!this.isShowSearchSelected()) {
      this.isShowForm = true;
      this.store$.dispatch(new searchBoxActions.Hide());
    }
  }
}
